/**
 * Service to report A/B experiment assignments to the backend (py-insights-api via gateway).
 * The backend extracts userId and company_id from the Bearer token; it stores data in Redis
 * and exposes a report with percentages and company_ids per group.
 *
 * Supports the new screen-level model: when `screenKey` is provided the backend resolves
 * which experiment the user belongs to on that screen and returns `enabled: true/false`.
 */

import { apiGateway } from "@/_old/services/api-gateway";

const ASSIGNMENTS_ENDPOINT = "/ab-experiments/assignments";
const SCREENS_ENDPOINT = "/ab-experiments/screens";

export type AbAssignmentPayload = {
  experimentKey: string;
  variant: string;
  timestamp?: string;
  screenKey?: string;
  weights?: Record<string, number>;
};

export type AbScreenAssignmentResponse = {
  ok: boolean;
  enabled: boolean;
  variant?: string | null;
  assignedExperiment?: string;
};

export type AbScreenConfig = {
  version: number;
  rebalance_seed: string;
  experiments: Array<{ key: string; traffic: number; status: string }>;
};

/**
 * Fetches the current user's variant for an experiment from the backend (source of truth).
 *
 * **Without screenKey (legacy):** returns `{ variant }` or null (404).
 *
 * **With screenKey:** returns `{ enabled, variant, assignedExperiment }`.
 * - `enabled: false` means the user belongs to a different experiment on this screen.
 * - `enabled: true` + `variant` is the assigned variant (may be null if not yet created).
 */
export async function fetchAbAssignment(
  experimentKey: string,
  screenKey?: string,
): Promise<{
  variant: string;
  enabled?: boolean;
  assignedExperiment?: string;
} | null> {
  try {
    let url = `${ASSIGNMENTS_ENDPOINT}?experimentKey=${encodeURIComponent(experimentKey)}`;
    if (screenKey) {
      url += `&screenKey=${encodeURIComponent(screenKey)}`;
    }
    const res = await apiGateway.get<
      { variant: string } | AbScreenAssignmentResponse
    >(url);
    return (res?.data as any) ?? null;
  } catch {
    return null;
  }
}

/**
 * Sends the assignment to the backend (apiGateway → py-insights-api).
 *
 * **Without screenKey (legacy):** records the assignment directly.
 *
 * **With screenKey:** the backend resolves screen assignment first.
 * - If the experiment is not the one assigned to this user on the screen → `{ enabled: false }`.
 * - If it matches → resolves/creates variant → `{ enabled: true, variant: "..." }`.
 *
 * @param weights Optional variant weights for server-side variant selection (e.g. `{ A: 50, B: 50 }`).
 */
export async function reportAbAssignment(
  experimentKey: string,
  variant: string,
  screenKey?: string,
  weights?: Record<string, number>,
): Promise<AbScreenAssignmentResponse | { variant: string } | null> {
  try {
    const payload: AbAssignmentPayload = {
      experimentKey,
      variant,
      timestamp: new Date().toISOString(),
    };
    if (screenKey) payload.screenKey = screenKey;
    if (weights) payload.weights = weights;

    const res = await apiGateway.post<AbScreenAssignmentResponse>(
      ASSIGNMENTS_ENDPOINT,
      payload,
    );
    return res?.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Fetches the screen config (experiments, traffic weights, version).
 * Useful for the frontend to know which experiments are active on a given screen.
 */
export async function fetchScreenConfig(
  screenKey: string,
): Promise<AbScreenConfig | null> {
  try {
    const res = await apiGateway.get<AbScreenConfig>(
      `${SCREENS_ENDPOINT}/${encodeURIComponent(screenKey)}`,
    );
    return res?.data ?? null;
  } catch {
    return null;
  }
}

export type AbExperimentUserAssignment = {
  company_id: number;
  user_id: number | string;
};

export type AbExperimentVariantReport = {
  variant: string;
  count: number;
  percentage: number;
  users: AbExperimentUserAssignment[];
};

export type AbExperimentReportItem = {
  experimentKey: string;
  name: string;
  total: number;
  variants: AbExperimentVariantReport[];
};
